import { useRef } from 'react';

interface PhotoUploadSplitControlsProps {
  uploading: boolean;
  onFilesSelected: (files: FileList) => Promise<void> | void;
}

interface StackedPhotoUploadSplitControlsProps extends PhotoUploadSplitControlsProps {
  uploadProgress?: {
    current: number;
    total: number;
  };
}

async function handleInputChange(
  input: HTMLInputElement,
  onFilesSelected: (files: FileList) => Promise<void> | void,
): Promise<void> {
  if (input.files && input.files.length > 0) {
    await onFilesSelected(input.files);
  }
  input.value = '';
}

export function SquarePhotoUploadSplitControls({
  uploading,
  onFilesSelected,
}: PhotoUploadSplitControlsProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        onClick={() => cameraInputRef.current?.click()}
        disabled={uploading}
        className="w-20 h-20 lg:w-24 lg:h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-td-blue transition-colors disabled:opacity-50"
        aria-label="Foto maken"
      >
        {uploading ? (
          <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Uploaden...</span>
          </div>
        ) : (
          <>
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-[10px] font-medium text-gray-600 leading-none">Foto maken</span>
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => libraryInputRef.current?.click()}
        disabled={uploading}
        className="w-20 h-20 lg:w-24 lg:h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-td-blue transition-colors disabled:opacity-50"
        aria-label="Foto's uit bibliotheek"
      >
        {uploading ? (
          <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Uploaden...</span>
          </div>
        ) : (
          <>
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[10px] font-medium text-gray-600 leading-none">Bibliotheek</span>
          </>
        )}
      </button>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => { void handleInputChange(e.currentTarget, onFilesSelected); }}
        className="hidden"
        aria-label="Foto maken met camera"
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={(e) => { void handleInputChange(e.currentTarget, onFilesSelected); }}
        className="hidden"
        aria-label="Selecteer foto's uit bibliotheek"
      />
    </>
  );
}

export function StackedPhotoUploadSplitControls({
  uploading,
  uploadProgress = { current: 0, total: 0 },
  onFilesSelected,
}: StackedPhotoUploadSplitControlsProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="mb-4 space-y-2">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => { void handleInputChange(e.currentTarget, onFilesSelected); }}
        className="hidden"
        disabled={uploading}
        aria-label="Foto maken met camera"
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={(e) => { void handleInputChange(e.currentTarget, onFilesSelected); }}
        className="hidden"
        disabled={uploading}
        aria-label="Selecteer foto's uit bibliotheek"
      />

      <button
        type="button"
        onClick={() => cameraInputRef.current?.click()}
        disabled={uploading}
        className={`flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border-2 border-dashed transition-colors ${
          uploading
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-td-blue/40 bg-td-blue/5 hover:border-td-blue hover:bg-td-blue/10'
        }`}
      >
        {uploading ? (
          <>
            <svg className="w-5 h-5 text-td-muted animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm text-td-muted">
              Uploaden... {uploadProgress.total > 1 && `(${uploadProgress.current}/${uploadProgress.total})`}
            </span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5 text-td-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm font-semibold text-td-blue">Foto maken</span>
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => libraryInputRef.current?.click()}
        disabled={uploading}
        className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl border-2 border-dashed transition-colors ${
          uploading
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-td-blue/60 hover:bg-td-blue/5'
        }`}
      >
        <svg className="w-5 h-5 text-td-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="text-sm font-medium text-td-muted">Uit bibliotheek</span>
      </button>
    </div>
  );
}
